/**
 * Tabblad Beheerders: scholen, en per school de leerkrachten.
 *
 * Na elke wijziging aan een leerkracht wordt de lijst scholen opnieuw
 * opgehaald en de gekozen school opnieuw gezocht, want de leerkrachten
 * zitten in het schoolrecord.
 */
import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import { basis } from '../stijlen';

const LEGE_SCHOOL = { naam: '', brinnummer: '' };
const LEGE_LEERKRACHT = { naam: '', username: '', password: '', email: '' };

export function Beheerders({ api }) {
  const [scholen, setScholen] = useState([]);
  const [laden, setLaden] = useState(false);
  const [school, setSchool] = useState(null);
  const [nieuweSchool, setNieuweSchool] = useState(null);
  const [nieuweLeerkracht, setNieuweLeerkracht] = useState(null);
  const [bewerkt, setBewerkt] = useState(null);

  const haalScholen = async (behoudGekozen = false) => {
    if (!behoudGekozen) setLaden(true);
    try {
      const data = await api.haalScholen();
      if (data.success) {
        setScholen(data.data);
        if (behoudGekozen && school) {
          const bijgewerkt = data.data.find(s => s.id === school.id);
          if (bijgewerkt) setSchool(bijgewerkt);
        }
      }
    } catch (err) { console.error('Fout:', err); }
    finally { setLaden(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { haalScholen(); }, []);

  const maakSchool = async (e) => {
    e.preventDefault();
    try {
      const data = await api.maakSchool(nieuweSchool);
      if (data.success) { setNieuweSchool(null); haalScholen(); }
    } catch (err) { console.error('Fout:', err); }
  };

  const verwijderSchool = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze school wilt verwijderen?\n\nDit verwijdert ook:\n• Alle leerkrachten\n• Alle klassen\n• Alle leerlingen\n• Alle sessies en discussies\n• Alle stellingen en bronnen\n\nDeze actie kan niet ongedaan worden gemaakt.')) return;
    try {
      const data = await api.verwijderSchool(id);
      if (data.success) {
        setSchool(null);
        haalScholen();
        alert('School en alle bijbehorende gegevens zijn verwijderd.');
      } else {
        alert('Fout bij verwijderen: ' + (data.detail || 'Onbekende fout'));
      }
    } catch (err) {
      console.error('Fout:', err);
      alert('Er is een fout opgetreden bij het verwijderen van de school. Probeer het opnieuw.');
    }
  };

  const maakLeerkracht = async (e) => {
    e.preventDefault();
    const leerkracht = { ...nieuweLeerkracht, school_id: school.id };
    try {
      const data = await api.maakLeerkracht(leerkracht);
      if (data.success) {
        alert('Leerkracht aangemaakt!\n\nInloggegevens:\nGebruikersnaam: ' + leerkracht.username + '\nWachtwoord: ' + leerkracht.password + '\n\nBewaar deze gegevens veilig!');
        setNieuweLeerkracht(null);
        haalScholen(true);
      } else {
        alert('Fout bij toevoegen: ' + (data.detail || 'Onbekende fout'));
      }
    } catch (err) {
      console.error('Fout:', err);
      alert('Er ging iets mis bij het toevoegen van de leerkracht');
    }
  };

  const verwijderLeerkracht = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze leerkracht wilt verwijderen?')) return;
    try {
      const data = await api.verwijderLeerkracht(id);
      if (data.success) haalScholen(true);
    } catch (err) { console.error('Fout:', err); }
  };

  const slaLeerkrachtOp = async (e) => {
    e.preventDefault();
    try {
      const data = await api.wijzigLeerkracht(bewerkt.id, {
        naam: bewerkt.naam, username: bewerkt.username, password: bewerkt.newPassword || null,
      });
      if (data.success) { setBewerkt(null); haalScholen(true); }
    } catch (err) { console.error('Fout:', err); }
  };

  return (
    <div style={styles.splitView}>
      <div style={styles.listPanel}>
        <div style={styles.panelHeader}>
          <h2>Scholen</h2>
          <button onClick={() => setNieuweSchool(LEGE_SCHOOL)} style={styles.addBtn}><Plus size={16} /> Nieuwe School</button>
        </div>
        {nieuweSchool && (
          <form onSubmit={maakSchool} style={styles.form}>
            <input type="text" placeholder="Brinnummer" value={nieuweSchool.brinnummer} onChange={(e) => setNieuweSchool({ ...nieuweSchool, brinnummer: e.target.value })} style={styles.input} required />
            <input type="text" placeholder="Schoolnaam" value={nieuweSchool.naam} onChange={(e) => setNieuweSchool({ ...nieuweSchool, naam: e.target.value })} style={styles.input} required />
            <div style={styles.formButtons}>
              <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
              <button type="button" onClick={() => setNieuweSchool(null)} style={styles.cancelBtn}>Annuleren</button>
            </div>
          </form>
        )}
        {laden ? <p>Laden...</p> : scholen.length === 0 ? <p style={styles.empty}>Nog geen scholen.</p> : (
          <ul style={styles.list}>
            {scholen.map((s) => (
              <li key={s.id} style={school?.id === s.id ? styles.listItemActive : styles.listItem} onClick={() => setSchool(s)}>
                <span>{s.brinnummer} - {s.naam}</span>
                <button onClick={(e) => { e.stopPropagation(); verwijderSchool(s.id); }} style={styles.deleteBtn}><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div style={styles.detailPanel}>
        {school ? (
          <>
            <div style={styles.panelHeader}>
              <h2>{school.naam}</h2>
              <button onClick={() => setNieuweLeerkracht(LEGE_LEERKRACHT)} style={styles.addBtn}><Plus size={16} /> Nieuwe Leerkracht</button>
            </div>
            <p style={styles.beschrijving}>Brinnummer: {school.brinnummer || '-'}</p>

            {nieuweLeerkracht && (
              <form onSubmit={maakLeerkracht} style={styles.form}>
                <input type="text" placeholder="Naam leerkracht" value={nieuweLeerkracht.naam} onChange={(e) => setNieuweLeerkracht({ ...nieuweLeerkracht, naam: e.target.value })} style={styles.input} required />
                <input type="email" placeholder="E-mailadres" value={nieuweLeerkracht.email} onChange={(e) => setNieuweLeerkracht({ ...nieuweLeerkracht, email: e.target.value })} style={styles.input} required />
                <input type="text" placeholder="Gebruikersnaam" value={nieuweLeerkracht.username} onChange={(e) => setNieuweLeerkracht({ ...nieuweLeerkracht, username: e.target.value })} style={styles.input} autoComplete="off" required />
                <input type="password" placeholder="Wachtwoord" value={nieuweLeerkracht.password} onChange={(e) => setNieuweLeerkracht({ ...nieuweLeerkracht, password: e.target.value })} style={styles.input} autoComplete="new-password" required />
                <div style={styles.formButtons}>
                  <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                  <button type="button" onClick={() => setNieuweLeerkracht(null)} style={styles.cancelBtn}>Annuleren</button>
                </div>
              </form>
            )}

            <h3 style={styles.subheader}>Leerkrachten</h3>
            <div style={{ height: '12px' }}></div>
            {bewerkt ? (
              <form onSubmit={slaLeerkrachtOp} style={styles.form}>
                <h4>Leerkracht bewerken</h4>
                <div style={{ height: '8px' }}></div>
                <label style={styles.label}>Naam</label>
                <input type="text" value={bewerkt.naam} onChange={(e) => setBewerkt({ ...bewerkt, naam: e.target.value })} style={styles.input} required />
                <label style={styles.label}>Gebruikersnaam</label>
                <input type="text" value={bewerkt.username} onChange={(e) => setBewerkt({ ...bewerkt, username: e.target.value })} style={styles.input} autoComplete="off" required />
                <label style={styles.label}>Nieuw wachtwoord</label>
                <input type="password" value={bewerkt.newPassword || ''} onChange={(e) => setBewerkt({ ...bewerkt, newPassword: e.target.value })} style={styles.input} autoComplete="new-password" placeholder="••••••••" />
                <div style={styles.formButtons}>
                  <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                  <button type="button" onClick={() => setBewerkt(null)} style={styles.cancelBtn}>Annuleren</button>
                </div>
              </form>
            ) : school.teachers?.length === 0 ? <p style={styles.empty}>Nog geen leerkrachten.</p> : (
              <ul style={styles.list}>
                {school.teachers?.map((leerkracht) => (
                  <li key={leerkracht.id} style={styles.listItem}>
                    <span>{leerkracht.naam} ({leerkracht.username})</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => setBewerkt({ ...leerkracht, newPassword: '' })} style={styles.addBtn}>Bewerken</button>
                      <button onClick={() => verwijderLeerkracht(leerkracht.id)} style={styles.deleteBtn}><Trash2 size={14} /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : <p style={styles.empty}>Selecteer een school</p>}
      </div>
    </div>
  );
}

const styles = { ...basis };
